/**
 * Theme hook.
 *
 * Three themes:
 *   - "rivercity" (default): light content + dark navy sidebar, brand palette
 *   - "light":              light content + light sidebar
 *   - "dark":               dark content + dark sidebar (full dark mode)
 *
 * Theme is stored in localStorage (per-device). If we later want it to sync
 * across devices, add a `theme` column to `users` and a `PATCH /auth/preferences`
 * endpoint; hydrate this hook from `authApi.me()`.
 */
import { useCallback, useEffect, useState } from "react"

export type Theme = "rivercity" | "light" | "dark"

const STORAGE_KEY = "rc.theme"
const DEFAULT_THEME: Theme = "rivercity"

function readStored(): Theme {
  if (typeof window === "undefined") return DEFAULT_THEME
  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (raw === "rivercity" || raw === "light" || raw === "dark") return raw
  return DEFAULT_THEME
}

/**
 * Apply a theme to the root element. Safe to call before React mounts.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return
  document.documentElement.dataset.theme = theme
}

/**
 * One-shot: read from storage and apply. Call in main.tsx before render to
 * avoid a flash of the default theme.
 */
export function bootstrapTheme(): Theme {
  const t = readStored()
  applyTheme(t)
  return t
}

export function useTheme(): {
  theme: Theme
  setTheme: (t: Theme) => void
} {
  const [theme, setThemeState] = useState<Theme>(() => readStored())

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  const setTheme = useCallback((next: Theme) => {
    window.localStorage.setItem(STORAGE_KEY, next)
    setThemeState(next)
  }, [])

  return { theme, setTheme }
}
